import React, { useEffect, useRef, useState } from "react";
import { formatMoney } from "@shopify/theme-currency";
import { withCartContext } from "./cartContext.jsx";
import MiniCartItem from "./miniCartItem.jsx";
import MiniCartItemLoader from "./miniCartItemLoader.jsx";
import ShippingThreshold from "./shippingThreshold.jsx";
import Spinner from "./Spinner.jsx";
import { Swiper, SwiperSlide } from "swiper/react";
import SwiperCore, { Navigation } from "swiper";
import GiftModal from "./GiftModal.jsx";
import ShopPay from "./shopPay.jsx";
import { generateRandomKey } from "./minicart.jsx";

const CART_THRESHOLD = window.CART_THRESHOLD;

const MiniCartContent = ({
  cartState,
  cartLoading,
  panel,
  empty,
  productAdditionalCopy,
  locales,
  removeItem,
  customerTags,
}) => {
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [disabledCheckoutButton, setDisabledCheckoutButton] = useState(false);
  const [isTermsChecked, setIsTermsChecked] = useState(false);
  const [allItemsInStock, setAllItemsInStock] = useState(true);
  const [showGiftModal, setShowGiftModal] = useState(false);
  const [giftCheckbox, setGiftCheckbox] = useState(false);
  const [modalContent, setModalContent] = useState({
    from: "",
    to: "",
    message: "",
  });

  SwiperCore.use([Navigation]);
  const swiperParams = {
    loop: true,
    slidesPerView: "auto",
    spaceBetween: 10,
    navigation: {
      nextEl: ".mini-cart__items-empty_next",
      prevEl: null,
    },
  };
  useEffect(() => {
    // console.log("TEST Customer tags:", customerTags);
    const dataSubtotalEl = document.querySelector("#minicart-shoppay-subtotal");
    const bubbleEl = document.querySelector('.cart-count-bubble [aria-hidden="true"]');
    const bubbleHIddenEl = document.querySelector(".cart-count-bubble .visually-hidden");
    bubbleEl.innerText = cartState.item_count ?? bubbleEl.innerText;
    bubbleHIddenEl.innerText = `${cartState.item_count} items` ?? bubbleHIddenEl.innerText;
    dataSubtotalEl.innerHTML = formatMoney(cartState?.total_price ? cartState.total_price : 0);
    !!cartState?.attributes?.hasOwnProperty("gift-note") && setGiftCheckbox(true);
    cartIntegrityCheck();
  }, [cartState, customerTags]);

  const cartIntegrityCheck = () => {
    console.log("Checking Cart Integrity...");
    let itemsToRemove = {};
    let parentProductsFound = [];

    if (!cartState?.items) {
      return;
    }
    // Loop through all items with a parent variant
    cartState.items
      .filter((item) => item.properties?.["_Parent Variant"])
      .forEach((item) => {
        const parentId = item.properties["_Parent Variant"];
        const timestamp = item.properties["_Timestamp"];

        // If the parent product is already in the cart, skip it
        if (parentProductsFound.includes(parentId)) {
          return;
        }

        // Check if the parent product is in the cart
        const parentProductInCart = cartState.items.some(
          (i) =>
            !i.properties?.["_Parent Variant"] &&
            +i.properties["_Timestamp"] === +timestamp &&
            parentId === i.id
        );

        // If the parent product is not in the cart, add it to the list of products to remove
        if (!parentProductInCart) {
          itemsToRemove[item.key] = 0; // Ensure item.key exists and is unique
        } else {
          parentProductsFound.push(parentId); // Use parentId instead of timestamp
        }
      });
    if (Object.keys(itemsToRemove).length > 0) {
      console.log("Removing items from cart...", itemsToRemove);
      removeItem({ items: itemsToRemove });
    }
  };

  const checkItemsStock = () => {
    const itemPromises = cartState?.items.map((item) => {
      return fetch(`/products/${item.handle}.json`)
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Failed to fetch product ${item.handle}`);
          }
          return response.json();
        })
        .then((itemData) => {
          return itemData.product.variants.find((variant) => variant.id == item.variant_id);
        });
    });

    return Promise.all(itemPromises)
      .then((items) => {
        return items;
      })
      .catch((error) => {
        console.error(error); // log any errors that occurred during fetch requests
      });
  };
  // Set up array to check already included items against
  const processedItems = [];
  // Create array of formatted cart items
  const cartItems =
    cartState?.items
      ?.map((item) => {
        // If sample product, skip
        if (item.properties?.["_Parent Variant"]) return;

        // If item already processed, skip
        if (processedItems.includes(item.key)) return;

        return item;
      })
      .filter((i) => i) || [];
  const tigerEloiseVendor = "tiger & eloise";
  const SCLItems = cartItems.filter(
    (item) => item.vendor.toLowerCase().replace(/&amp;/g, "&") !== tigerEloiseVendor
  );
  const TigerEloiseItems = cartItems.filter(
    (item) => item.vendor.toLowerCase().replace(/&amp;/g, "&") === tigerEloiseVendor
  );
  const handleCheckout = () => {
    setCheckoutLoading(true);
    checkItemsStock()
      .then((items) => {
        let itemsInStock = true;
        if (items?.inventory_quantity !== undefined) {
          itemsInStock = items?.every(
            (item) => item.inventory_quantity > 0 || item.inventory_policy === "continue"
          );
        }
        setAllItemsInStock(itemsInStock);
        if (itemsInStock) {
          setAllItemsInStock(true);
          location.href = "/checkout";
        }
      })
      .catch((error) => {
        console.error(error);
        setAllItemsInStock(false);
        setDisabledCheckoutButton(true);
        setCheckoutLoading(false);
      });
  };
  const handleTermsCheckboxChange = (e) => {
    setDisabledCheckoutButton(e.target.checked);
  };
  const handleModalOpen = () => {
    setShowGiftModal(true);
  };
  const handleModalClose = () => {
    setShowGiftModal(false);
    if (modalContent.from === "" || modalContent.to === "" || modalContent.message === "") {
      setGiftCheckbox(false);
    }
  };
  const handleModalInputChange = (event) => {
    const { name, value } = event.target;
    setModalContent({ ...modalContent, [name]: value });
  };

  const handleModalFormSubmit = async (event) => {
    event.preventDefault();

    if (modalContent.from && modalContent.to && modalContent.message) {
      const body = {
        attributes: {
          "gift-note": modalContent.message,
          to: modalContent.to,
          from: modalContent.from,
        },
      };

      const options = {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      };

      await fetch("/cart/update.js", options);
      handleModalClose();
    }
  };
  const removeCartGiftAttributes = async () => {
    const body = {
      attributes: {
        "gift-note": null,
        to: null,
        from: null,
      },
    };

    const options = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    };

    await fetch("/cart/update.js", options);
  };
  const handleGiftCheckboxChange = (e) => {
    if (e.target.checked === true) {
      handleModalOpen();
    } else {
      removeCartGiftAttributes();
    }
    setGiftCheckbox(!giftCheckbox);
  };
  const handleTermsChevronClick = () => {
    setIsTermsChecked(!isTermsChecked);
  };

  const handleCloseMiniCart = () => {
    document.querySelector("[data-panel-close]").click();
  };
  return (
    <div className={`mini-cart__wrapper mini-cart__wrapper--panel-${panel}`}>
      <div className="mini-cart__header">
        <h2 className="text-h5">
          Your Bag
          {cartState?.item_count > 0 ? (
            <span className="text-h5">
              {" "}
              - {cartState?.item_count ? cartState?.item_count : "0"} Item
              {cartState.item_count > 1 ? "s" : null}
            </span>
          ) : null}
        </h2>
      </div>
      <div className="free-shipping-message-container" style={{ maxHeight: "30px" }}>
        {cartState?.total_price < CART_THRESHOLD && cartState?.total_price > 0 ? (
          <div
            className="free-shipping-message-to-qualify"
            style={{
              position: "relative",
              padding: "0px 0px 10px 0px",
            }}
          >
            You Are{" "}
            <span
              className="mini-cart__label-price"
              style={{
                fontWeight: 500,
              }}
            >
              {formatMoney(CART_THRESHOLD - cartState.total_price)}
            </span>{" "}
            Away from Free Shipping
            <div
              className="border-bottom"
              style={{
                position: "absolute",
                bottom: "0",
                left: "10%", // Adjust the left value to center it
                right: "10%", // Adjust the right value to center it
                height: "4px",
                backgroundColor: "#ff6491",
                display: "block",
                borderRadius: "10px",
              }}
            >
              {/* Remaining Progress */}
              <div
                className="progress-bar-remaining"
                style={{
                  height: "100%",
                  width: `${100 - (cartState.total_price / CART_THRESHOLD) * 100}%`,
                  backgroundColor: "#e4e4e4",
                  display: "block",
                  position: "absolute",
                  top: 0,
                  left: `${(cartState.total_price / CART_THRESHOLD) * 100 + 0.5}%`,
                  borderRadius: "10px",
                  borderTopLeftRadius: "0px",
                  borderBottomLeftRadius: "0px",
                }}
              />
            </div>
          </div>
        ) : cartState?.total_price >= CART_THRESHOLD ? (
          <p
            className="free-shipping-message"
            style={{
              whiteSpace: "nowrap",
              textAlign: "center",
              color: "#FF5786",
              fontWeight: "bold",
            }}
          >
            You Qualify for Free Shipping!! 🎉
          </p>
        ) : null}
      </div>

      <div className="mini-cart__panels">
        <div className="mini-cart__content-wrapper">
          <div
            className={`mini-cart__content ${cartItems.length === 0 ? "empty-cart" : ""}`}
            id="mini-cart__drawer"
          >
            <div className="mini-cart__content-items">
              {!cartLoading ? (
                cartItems.length ? (
                  <>
                    <div className="mini-cart__content-items--inner">
                      <ShippingThreshold />
                      <div id="mini-cart__items-list">
                        {SCLItems.length > 0 && TigerEloiseItems.length > 0 && (
                          // <h4>{SCLItems[0].vendor}</h4>
                          <img
                            src="https://cdn.shopify.com/s/files/1/0096/2392/files/Logo_0a62020c-1e62-4c3b-b6d2-30d1294f65d2.png?v=1695843303"
                            style={{ width: "250px" }}
                          />
                        )}
                        {SCLItems.map((item) =>
                          !cartLoading ? (
                            <div
                              key={`cart-item-${generateRandomKey()}`}
                              className="mini-cart__item-group"
                            >
                              <MiniCartItem
                                item={item}
                                cartState={cartState}
                                productAdditionalCopy={productAdditionalCopy}
                              />
                            </div>
                          ) : (
                            <MiniCartItemLoader key={`cart-item-loader-${item.id}`} />
                          )
                        )}
                        {TigerEloiseItems.length > 0 && SCLItems.length > 0 && (
                          // <h4 style={{ marginTop: "12px" }}>{TigerEloiseItems[0].vendor}</h4>
                          <img
                            src="https://cdn.shopify.com/s/files/1/0096/2392/files/logo-tiger-eloise_b9eb4319-b9b1-422a-acfb-12e387457d80.png?v=1694611978"
                            style={{ width: "110px", marginTop: "20px" }}
                          />
                        )}
                        {TigerEloiseItems.map((item) =>
                          !cartLoading ? (
                            <div
                              key={`cart-item-${generateRandomKey()}`}
                              className="mini-cart__item-group"
                            >
                              {
                                <MiniCartItem
                                  item={item}
                                  cartState={cartState}
                                  productAdditionalCopy={productAdditionalCopy}
                                />
                              }
                            </div>
                          ) : (
                            <MiniCartItemLoader key={`cart-item-loader-${item.id}`} />
                          )
                        )}
                      </div>
                    </div>

                    <div className="nosto_element" id="Nosto-Mini-Cart-Placement"></div>

                    <div className="mini-cart__footer">
                      <div>{locales?.shipping_and_taxes_text?.replace(/&amp;/g, "&")}</div>
                      <div>
                        {showGiftModal && (
                          <GiftModal
                            modalContent={modalContent}
                            handleModalFormSubmit={handleModalFormSubmit}
                            handleModalInputChange={handleModalInputChange}
                            handleModalClose={handleModalClose}
                            locales={locales}
                          />
                        )}
                        <div className={`checkbox-input-group`}>
                          <input
                            checked={giftCheckbox}
                            onChange={handleGiftCheckboxChange}
                            type="checkbox"
                            id="gift_wrap"
                            tabIndex="0"
                          />
                          <label htmlFor="gift_wrap">
                            {locales?.send_as_a_gift_label?.replace(/&amp;/g, "&")}
                          </label>
                        </div>
                      </div>
                      <div>
                        <a className="cart-link" href="/cart">
                          Go To Cart
                        </a>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="mini-cart__items-empty">
                      <ShippingThreshold />
                      <div className="mini-cart__items-empty-text">
                        <div className="mini-cart__items-empty-title text-h6">
                          {empty?.cartEmptySubHeader}
                        </div>
                        {empty?.cartEmptyButtonUrl !== undefined &&
                        empty?.cartEmptyButtonTitle !== undefined ? (
                          empty?.cartEmptyButtonUrl ? (
                            <a href={empty.cartEmptyButtonUrl} className="button button--small">
                              <span className="button-title text-label">
                                {empty.cartEmptyButtonTitle}
                              </span>
                            </a>
                          ) : (
                            <button onClick={handleCloseMiniCart} className="button button--small">
                              <span className="button-title text-label">
                                {empty.cartEmptyButtonTitle}
                              </span>
                            </button>
                          )
                        ) : null}
                      </div>
                    </div>
                    {empty?.cartEmptyProductRecommendations.length > 0 ? (
                      <div className="mini-cart__items-empty-products">
                        <p className="text-h3">{empty?.cartEmptyProductsTitle}</p>
                        <Swiper {...swiperParams}>
                          {empty?.cartEmptyProductRecommendations.map((product) => (
                            <SwiperSlide key={`mini-cart__items-empty-product-${product.id}`}>
                              <div className="mini-cart__items-empty-product">
                                <a href={"/products/" + product.handle}>
                                  <img src={product.featured_image} width={160} loading={"lazy"} />
                                </a>
                                <div className="mini-cart__items-empty-product-info">
                                  <a href={"/products/" + product.handle}>
                                    <p className="text-body-2">{product.title}</p>
                                  </a>
                                  <p className="text-body-2">{formatMoney(product.price)}</p>
                                </div>
                              </div>
                            </SwiperSlide>
                          ))}
                          <a
                            href="#"
                            className="mini-cart__items-empty_next"
                            tabIndex="0"
                            role="button"
                            aria-label="Next slide"
                            aria-controls="swiper-wrapper-231649512c31e8a3"
                          >
                            <svg
                              width="37"
                              height="37"
                              viewBox="0 0 37 37"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <circle cx="18.5" cy="18.5" r="18.5" fill="#FFE2E8" />
                              <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M9 18L22.5908 18C19.7288 16.0116 17.8547 12.7263 17.8548 9L19.8548 9C19.8547 13.957 23.9355 18 28.9997 18L28.9997 18L29 18L29 18L29 20L29 20C23.9358 20 19.8551 24.043 19.8551 29L17.8551 29C17.8551 25.2737 19.7291 21.9884 22.5911 20L9 20L9 18Z"
                                fill="#2C272D"
                              />
                            </svg>
                          </a>
                        </Swiper>
                      </div>
                    ) : null}
                  </>
                )
              ) : (
                <div className="mini-cart__items-empty">
                  <div className="mini-cart__loader">
                    <Spinner />
                  </div>
                </div>
              )}
            </div>
          </div>
          {cartItems.length ? (
            <>
              <div
                className={`mini-cart__total ${
                  cartState?.total_price > 0 ? "" : "mini-cart__total--empty"
                }`}
              >
                <div className="mini-cart__total-top">
                  <div className="mini-cart__label mini-cart__label-title">Subtotal</div>
                  <div
                    className="mini-cart__label mini-cart__label-price"
                    id="mini-cart__total-price"
                  >
                    {formatMoney(cartState?.total_price ? cartState.total_price : 0)}
                  </div>
                </div>
                <div>
                  <div className="terms">
                    <div className={`checkbox-input-group ${isTermsChecked ? "active" : ""}`}>
                      <input
                        onChange={handleTermsCheckboxChange}
                        type="checkbox"
                        id="shipping_terms"
                        tabIndex="0"
                      />
                      <label htmlFor="shipping_terms">
                        {locales?.terms_agree_checkbox_label?.replace(/&amp;/g, "&")}
                      </label>
                      <button
                        type={"button"}
                        className="terms-chevron"
                        onClick={handleTermsChevronClick}
                        aria-label="Show Terms and Conditions"
                      >
                        <svg
                          width="16"
                          height="14"
                          viewBox="0 0 16 14"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M2.38965 4.29175L8.05851 9.16675L13.7274 4.29175"
                            stroke="#2C272D"
                          />
                        </svg>
                      </button>
                    </div>
                    <div
                      className="terms-copy"
                      dangerouslySetInnerHTML={{
                        __html: locales?.terms_copy_html?.replace(/&amp;/g, "&"),
                      }}
                    ></div>
                  </div>
                </div>
                {allItemsInStock ? null : (
                  <p className="checkout--error">
                    One or more items in your cart are out of stock.
                  </p>
                )}
                <button
                  className="button button--primary button--shape button--fixed-height"
                  onClick={handleCheckout}
                  disabled={!(cartState?.total_price <= 0 || disabledCheckoutButton)}
                >
                  {checkoutLoading ? (
                    <span className="icon">
                      <Spinner />
                    </span>
                  ) : (
                    <span className="button-title">CHECKOUT</span>
                  )}
                </button>
                <loop-onstore />
                <ShopPay />
                {/* <div className="nosto_element" id="Nosto-Mini-Cart-Placement"></div> */}
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default withCartContext(MiniCartContent);
